import { BarChart2 } from 'react-feather';
import { ChangeEvent, useEffect, useMemo, useRef } from 'react';
import { DocumentNode } from 'graphql';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import {
  ExportFormat, ExportRegistrationsMutation as ExportRegistrations,
  RegistrationFilters,
} from '__generated__/graphql';
import { useNotifier } from '../../common/Notifications/NotificationProvider';
import ExportRegistrationsMutation from './ExportRegistrationsMutation';
import GetExportProfilesQuery from '../Exports/GetExportProfilesQuery';
import GetExportsQuery from '../Exports/GetExportsQuery';
import GetMailchimpAudiencesQuery from './GetMailchimpAudiencesQuery';
import UI from '../../common/UI';
import useLocale from '../../common/useLocale';
import useMutation from '../../api/useMutation';
import useProject from '../useProject';
import useQuery from '../../api/useQuery';

export interface ExportRegistrationsFormProps {
  filters: RegistrationFilters;
  registrationsCount: number;
  onSuccess?: (exported: ExportRegistrations['exportRegistrations']) => void;
  refetchQueries?: DocumentNode[];
}

const ExportRegistrationsForm = ({
  filters, registrationsCount, onSuccess,
  refetchQueries = [GetExportsQuery],
}: ExportRegistrationsFormProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate, formatNumber } = useLocale();
  const project = useProject();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      export_profile_type: 'none',
      export_profile: null,
      columns: [],
      options: {
        format: ExportFormat.excel,
        mailchimp: {
          audience_id: null,
          tag: null,
        },
      },
    },
  });

  const handleExportProfileTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;

    if (value === 'none') {
      form.set((values) => ({
        ...values,
        export_profile_type: 'none',
        export_profile: null,
      }));
    } else if (value === 'create_new') {
      form.set((values) => ({
        ...values,
        export_profile_type: 'create_new',
        export_profile: {
          title: '',
        },
      }));
    } else if (value) {
      form.set((values) => ({
        ...values,
        export_profile_type: value,
        export_profile: {
          id: value,
        },
      }));
    }
  };

  const enabledColumns = form.values.columns.reduce(
    (values, { column, title, enabled }) => (enabled ? [...values, { column, title }] : values),
    [],
  );

  const [exportRegistrations, { loading, error }] = useMutation(
    ExportRegistrationsMutation,
    {
      variables: {
        input: {
          project: project.id,
          export_profile: form.values.export_profile,
          columns: enabledColumns,
          filters,
          options: {
            ...form.values.options,
            // Unset the Mailchimp config when not using Mailchimp
            ...(form.values.options.format !== ExportFormat.mailchimp && { mailchimp: undefined }),
          },
        },
      },
      onCompleted: ({ exportRegistrations }) => {
        notifier.success(t('notifications.export_started'));
        onSuccess?.(exportRegistrations);
      },
      refetchQueries,
      awaitRefetchQueries: true,
    },
  );

  const eventId = (filters.events || []).length === 1 ? filters.events[0] : null;

  const tagInputRef = useRef<HTMLInputElement>();

  const {
    data: mailchimpData,
  } = useQuery(GetMailchimpAudiencesQuery, {
    variables: {
      project_id: project.id,
      event_id: eventId || '-', // Field can be skipped, but the argument is required.
      include_event: !!eventId,
    },
    onCompleted: () => {
      form.reset((values) => ({
        ...values,
        options: {
          ...values.options,
          mailchimp: {
            audience_id: null,
            tag: null,
          },
        },
      }));
    },
  });

  const {
    data: exportProfilesData,
  } = useQuery(GetExportProfilesQuery, {
    variables: {
      project_id: project.id,
      type: 'registrations',
      event_id: eventId,
    },
    onCompleted: (data) => {
      form.reset((values) => ({
        ...values,
        columns: data.project.default_export_columns.map((column) => ({
          column: column.id,
          title: '',
          enabled: false,
        })),
      }));
    },
  });

  const selectedExportProfile = exportProfilesData?.project.export_profiles.filter(
    (exportProfile) => exportProfile.id === form.values.export_profile?.id,
  )[0];

  const handleSelectAllColumns = () => {
    form.set((values) => ({
      ...values,
      columns: form.values.columns.map((value) => ({ ...value, enabled: true })),
    }));
  };

  const handleDeselectAllColumns = () => {
    form.set((values) => ({
      ...values,
      columns: form.values.columns.map((value) => ({ ...value, enabled: false })),
    }));
  };

  const allColumnsSelected = form.values.columns.filter((value) => !value.enabled).length === 0;
  const noColumnsSelected = form.values.columns.filter((value) => value.enabled).length === 0;

  const audiences = useMemo(() => mailchimpData?.project.mailchimp_connection?.audiences || [], [mailchimpData]);
  const eventTitle = mailchimpData?.event?.title;
  const showMailchimp = audiences.length > 0;

  const isSpreadsheet = form.values.options.format === ExportFormat.excel
    || form.values.options.format === ExportFormat.csv;
  const isMailchimp = form.values.options.format === ExportFormat.mailchimp;

  // Pre-select the first Mailchimp audience and propose a tag.
  useEffect(() => {
    if (isMailchimp) {
      if (!form.values.options.mailchimp.audience_id) {
        const timestamp = formatDate(new Date(), { format: 'display_compact_date_time' });
        const tag = `${eventTitle || 'Atleta'} | ${timestamp}`;

        form.set('options.mailchimp.audience_id', audiences[0].id);
        form.set('options.mailchimp.tag', tag);
        tagInputRef.current.value = tag;
      }
    }
  }, [isMailchimp, form.values.options.mailchimp.audience_id, form, audiences, eventTitle, formatDate]);

  if (!exportProfilesData || !mailchimpData) {
    return <UI.PageLoader />;
  }

  return (
    <UI.Form onSubmit={() => exportRegistrations()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4>
            {t('export_registrations')}
          </UI.H4>
          {!showMailchimp && (
            <UI.Paragraph sc={{ mb: 0, mt: 2 }}>
              {t('export_registrations_description')}
            </UI.Paragraph>
          )}
        </UI.Div>

        <UI.HR />

        <UI.ServerErrors error={error} />

        {showMailchimp && (
          <UI.FormGrid sc={{ gutter: 0.25 }}>
            <UI.Radio
              onChange={() => form.set('options.format', ExportFormat.excel)}
              checked={isSpreadsheet}
              sc={{ box: true }}
              style={{ opacity: 1 }}
            >
              <UI.Div>
                <UI.Strong sc={{ block: true }}>
                  {t('spreadsheet')}
                </UI.Strong>
                {t('export_registrations_description')}
              </UI.Div>
            </UI.Radio>
            <UI.Radio
              onChange={() => form.set('options.format', ExportFormat.mailchimp)}
              checked={isMailchimp}
              sc={{ box: true }}
              style={{ opacity: 1 }}
            >
              <UI.Div>
                <UI.Strong sc={{ block: true }}>
                  {t('mailchimp')}
                </UI.Strong>
                {t('export_mailchimp_description')}
              </UI.Div>
            </UI.Radio>
          </UI.FormGrid>
        )}

        {isSpreadsheet && (
          <UI.FormGrid>
            <UI.InputGroup>
              <UI.InputLabel htmlFor="export_profile_type">
                {t('export_profile')}
              </UI.InputLabel>
              <UI.Select
                value={form.values.export_profile_type}
                onChange={handleExportProfileTypeChange}
                id="export_profile_type"
              >
                <option value="none">
                  {t('all_columns')}
                </option>
                <option value="create_new">
                  {t('custom_columns')}
                </option>
                {exportProfilesData?.project.export_profiles.map((exportProfile) => (
                  <option value={exportProfile.id} key={exportProfile.id}>
                    {exportProfile.title}
                    {' • '}
                    {t('n_columns', {
                      value: formatNumber(exportProfile.column_titles.length),
                      count: exportProfile.column_titles.length,
                    })}
                    {' • '}
                    {formatDate(parseDate(exportProfile.created_at), { format: 'display_compact_date' })}
                  </option>
                ))}
              </UI.Select>
            </UI.InputGroup>

            {form.values.export_profile_type === 'create_new' && (
              <UI.InputGroup>
                <UI.InputLabel>
                  {t('columns')}
                  <UI.Span sc={{ muted: true }}> • </UI.Span>
                  <UI.A
                    onClick={() => handleSelectAllColumns()}
                    sc={{ disabled: allColumnsSelected }}
                  >
                    {t('select_all')}
                  </UI.A>
                  <UI.Span sc={{ muted: true }}> • </UI.Span>
                  <UI.A
                    onClick={() => handleDeselectAllColumns()}
                    sc={{ disabled: noColumnsSelected }}
                  >
                    {t('deselect_all')}
                  </UI.A>
                </UI.InputLabel>
                <UI.InputDescription>
                  {t('create_export_profile_description')}
                </UI.InputDescription>
                <UI.FormGrid sc={{ gutter: 0.0625 }}>
                  {form.values.columns.map((value, index) => {
                    const column = exportProfilesData?.project.default_export_columns[index];

                    return (
                      <UI.FormGrid
                        sc={{
                          columns: '1fr 1fr',
                          gutter: 0.25,
                          background: 'gray.75',
                        }}
                        key={value.column}
                      >
                        <UI.Div style={{ minWidth: 0 }}>
                          <UI.Checkbox
                            {...form.checkbox(`columns.${index}.enabled`)}
                            sc={{
                              fontWeight: form.values.columns[index].enabled ? 500 : undefined,
                              pl: 6,
                              py: 2.5,
                              checkboxLeft: 12,
                              checkboxTop: 19,
                            }}
                          >
                            <UI.Truncate>
                              {column.title}
                            </UI.Truncate>
                          </UI.Checkbox>
                        </UI.Div>
                        <UI.Div sc={{ py: 1, pr: 2 }}>
                          <UI.Input
                            {...form.text(`columns.${index}.title`, { onChange: () => form.set(`columns.${index}.enabled`, true) })}
                            sc={{ size: 'md' }}
                            placeholder={column.title}
                          />
                        </UI.Div>
                      </UI.FormGrid>
                    );
                  })}
                </UI.FormGrid>
              </UI.InputGroup>
            )}

            {selectedExportProfile && (
              <UI.Info>
                <UI.Delimit>
                  {selectedExportProfile.column_titles}
                </UI.Delimit>
              </UI.Info>
            )}

            {form.values.export_profile_type === 'create_new' && (
              <UI.InputGroup>
                <UI.InputLabel htmlFor="add_export_profile_title">
                  {t('export_profile_title')}
                </UI.InputLabel>
                <UI.InputDescription>
                  {t('export_profile_title_description')}
                </UI.InputDescription>
                <UI.Input {...form.text('export_profile.title')} id="add_export_profile_title" />
              </UI.InputGroup>
            )}

            <UI.InputGroup>
              <UI.InputLabel htmlFor="add_export_format">
                {t('file_format')}
              </UI.InputLabel>
              <UI.Radio {...form.radio('options.format', ExportFormat.excel)} id="add_export_format">
                {t('export_formats.excel')}
              </UI.Radio>
              <UI.Radio {...form.radio('options.format', ExportFormat.csv)}>
                {t('export_formats.csv')}
              </UI.Radio>
            </UI.InputGroup>
          </UI.FormGrid>
        )}

        {isMailchimp && (
          <>
            <UI.FormGrid>
              {audiences.length > 1 && (
                <UI.InputGroup>
                  <UI.InputLabel htmlFor="MailchimpAudience">
                    {t('mailchimp_audience')}
                  </UI.InputLabel>
                  <UI.Select
                    {...form.select('options.mailchimp.audience_id', audiences.map((audience) => audience.id))}
                    id="MailchimpAudience"
                  >
                    {audiences.map((audience, index) => (
                      <option {...form.option(index)} key={index}>{audience.name}</option>
                    ))}
                  </UI.Select>
                </UI.InputGroup>
              )}

              <UI.InputGroup>
                <UI.InputLabel htmlFor="MailchimpTag">
                  {t('mailchimp_tag')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
                <UI.InputDescription>
                  {t('mailchimp_tag_description')}
                </UI.InputDescription>
                <UI.DebouncedInput
                  {...form.text('options.mailchimp.tag')}
                  ref={tagInputRef}
                  id="MailchimpTag"
                />
              </UI.InputGroup>
            </UI.FormGrid>

            <UI.Info>
              {t('mailchimp_export_once_per_email')}
            </UI.Info>
          </>
        )}

        <UI.HR />

        <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <UI.Div>
            <UI.Icon>
              <BarChart2 />
            </UI.Icon>
            {' '}
            {t('registrations')}
            {': '}
            {formatNumber(registrationsCount)}
          </UI.Div>
          <UI.Button
            type="submit"
            sc={{ brand: 'secondary' }}
            disabled={loading
              || (isMailchimp && !form.values.options.mailchimp.tag)
              || (isSpreadsheet && form.values.export_profile_type === 'create_new' && enabledColumns.length === 0)}
          >
            {t('export')}
          </UI.Button>
        </UI.FlexContainer>
      </UI.FormGrid>
    </UI.Form>
  );
};

export default ExportRegistrationsForm;
